import { Routes } from '@angular/router';
import { authGuard } from "./core/guards/auth/auth.guard";
import { isNotConnectedGuard } from "./core/guards/auth/is-not-connected.guard";
import { SubscriptionActiveGuard } from "./core/guards/subscription/subscription-active.guard";
import { isModuleEnabledGuard } from "./core/guards/subscription/is-module-enabled.guard";
import { waAdminGuard } from "./core/guards/access/wa-admin.guard";
import { developerHubGuard } from "./core/guards/access/developer-hub.guard";
import { defaultRouteExternalGuard } from "./core/guards/access/default-route-external.guard";
import { externalModuleAccessGuard } from './core/guards/access/external-module-access.guard';
import { internalOnlyGuard } from './core/guards/access/internal-only.guard';
import { maintenanceGuard, maintenancePageGuard } from "./core/maintenance/maintenance.guard";
import { Role } from "./models/enums";
import { PROJECT_MANAGEMENT_ROUTES } from './modules/project-management/project-management.route';
import { AuthCallbackComponent } from "./pages-utils/auth/auth-callback/auth-callback.component";
import { ResetPasswordComponent } from "./pages-utils/auth/reset-password/reset-password.component";
import { SignInComponent } from "./pages-utils/auth/sign-in/sign-in.component";
import { SetupOrganisationComponent } from "./pages-utils/setup-organisation/setup-organisation.component";

const routesSidebar: Routes = [
  {
    path: 'close',
    loadComponent: () => import('./pages-utils/sidebar-no-data/sidebar-no-data.component').then(m => m.SidebarNoDataComponent),
    outlet: 'sidebar',
  },
  {
    path: 'grid-details/:id',
    loadComponent: () => import('./modules/salaries/components/sidebar-grid-more/sidebar-grid-more.component').then(m => m.SidebarGridMoreComponent),
    outlet: 'sidebar',
  },
  {
    path: 'position-details/:id',
    loadComponent: () => import('./modules/salaries/components/sidebar-positions-more/sidebar-positions-more.component').then(m => m.SidebarPositionsMoreComponent),
    outlet: 'sidebar',
  },
  {
    path: 'certification-details/:id',
    loadComponent: () => import('./modules/salaries/components/sidebar-certifications-more/sidebar-certifications-more.component').then(m => m.SidebarCertificationsMoreComponent),
    outlet: 'sidebar',
  },
  {
    path: 'evolution-details',
    loadComponent: () => import('./modules/salaries/components/sidebar-salary-evolution/sidebar-salary-evolution.component').then(m => m.SidebarSalaryEvolutionComponent),
    outlet: 'sidebar',
  },
  {
    path: 'negotiation-details',
    loadComponent: () => import('./modules/salaries/components/sidebar-message/sidebar-message.component').then(m => m.SidebarMessageComponent),
    outlet: 'sidebar',
  },
  {
    path: 'inventory-details',
    loadComponent: () => import('./modules/inventories/components/sidebar-inventory-details/sidebar-inventory-details.component').then(m => m.SidebarInventoryDetailsComponent),
    outlet: 'sidebar',
  },
  {
    path: 'inventory-details/:id',
    loadComponent: () => import('./modules/inventories/components/sidebar-inventory-details/sidebar-inventory-details.component').then(m => m.SidebarInventoryDetailsComponent),
    outlet: 'sidebar',
  },
  {
    path: 'inventory-view/:id',
    loadComponent: () => import('./modules/inventories/components/sidebar-inventory-details-view/sidebar-inventory-details-view.component').then(m => m.SidebarInventoryDetailsViewComponent),
    outlet: 'sidebar',
  },
  {
    path: 'task-create',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-task/sidebar-task.component').then(m => m.SidebarTaskComponent),
    outlet: 'sidebar',
  },
  {
    path: 'task-edit/:id',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-task/sidebar-task.component').then(m => m.SidebarTaskComponent),
    outlet: 'sidebar',
  },
  {
    path: 'group-create',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-group/sidebar-group.component').then(m => m.SidebarGroupComponent),
    outlet: 'sidebar',
  },
  {
    path: 'group-edit/:id',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-group/sidebar-group.component').then(m => m.SidebarGroupComponent),
    outlet: 'sidebar',
  },
  // Panneau « Structure » d'un projet (groupes, lots, étiquettes, statuts).
  // `:onglet` = groupes | lots | etiquettes | statuts ; projet en query param `projectId`.
  {
    path: 'structure/:onglet',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-structure/sidebar-structure.component').then(m => m.SidebarStructureComponent),
    outlet: 'sidebar',
  },
  // Tags : meme motif que les groupes, la dialog modale a ete remplacee par une
  // sidenav. Le contexte (projet, catalogue, tag edite) passe par
  // ProjectTagSidebarService et non par l'URL : le catalogue n'a pas sa place
  // dans un parametre de route.
  {
    path: 'tag-create',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-tag/sidebar-tag.component').then(m => m.SidebarTagComponent),
    outlet: 'sidebar',
  },
  {
    path: 'tag-edit/:slug',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-tag/sidebar-tag.component').then(m => m.SidebarTagComponent),
    outlet: 'sidebar',
  },
  // Hub des projets (MOKA-214) : dossiers et historique complet d'un projet.
  {
    path: 'project-folder-create',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-project-folder/sidebar-project-folder.component').then(m => m.SidebarProjectFolderComponent),
    outlet: 'sidebar',
  },
  {
    path: 'project-folder-edit/:id',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-project-folder/sidebar-project-folder.component').then(m => m.SidebarProjectFolderComponent),
    outlet: 'sidebar',
  },
  {
    path: 'project-history/:id',
    loadComponent: () => import('./modules/project-management/sidebars/sidebar-project-history/sidebar-project-history.component').then(m => m.SidebarProjectHistoryComponent),
    outlet: 'sidebar',
  }
]


export let routes: Routes = [
  // ── Redirection racine → dashboard (protégé par canMatch de dashboard) ───────
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard'
  },
  // ── Route maintenance ─────────────────────────────────────────────────────────
  // maintenancePageGuard (et rien d'autre) : accessible quand MAINTENANCE_MODE=true
  // (destination de redirection de maintenanceGuard), sinon renvoie vers / pour ne
  // laisser personne bloqué sur la page une fois la maintenance terminée.
  {
    path: 'maintenance',
    loadComponent: () => import('./pages-utils/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
    title: 'Maintenance — Mokah+',
    canMatch: [maintenancePageGuard],
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./modules/dashboard/dashboard.routes').then(m => m.DashboardRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, SubscriptionActiveGuard, defaultRouteExternalGuard]
  },
  {
    path: 'setup-organisation',
    component: SetupOrganisationComponent,
    canMatch: [maintenanceGuard]
  },
  {
    path: 'calendar',
    loadChildren: () => import('./modules/calendar/calendar.routes').then(mod => mod.CalendarRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, SubscriptionActiveGuard, isModuleEnabledGuard, externalModuleAccessGuard],
    data: { moduleKey: 'calendars_enable', moduleExternalAccess: Role.CalendarsManager }
  },
  {
    path: 'salaries',
    loadChildren: () => import('./modules/salaries/salaries.routes').then(mod => mod.SalariesRoutes),
    canMatch: [maintenanceGuard],
    canActivateChild: [authGuard, SubscriptionActiveGuard, isModuleEnabledGuard, externalModuleAccessGuard],
    data: { moduleKey: 'human_resources_enable', moduleExternalAccess: Role.ComiteSalaryManager }
  },
  {
    path: 'materials',
    loadChildren: () => import('./modules/inventories/inventories.routes').then(mod => mod.InventoriesRoutes),
    canMatch: [maintenanceGuard],
    canActivateChild: [authGuard, SubscriptionActiveGuard, isModuleEnabledGuard, externalModuleAccessGuard],
    data: { moduleKey: 'inventory_enable', moduleExternalAccess: Role.InventoryManager, pageHeader: { breadcrumb: 'Inventaire', breadcrumbLink: false } }
  },
  {
    path: 'profile',
    loadChildren: () => import('./modules/profile/profile.routes').then(mod => mod.ProfileRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, SubscriptionActiveGuard, internalOnlyGuard],
    title: 'Profil',
    data: { authorizations: [], pageHeader: { breadcrumb: 'Profil', breadcrumbLink: false } }
  },
  {
    path: 'notifications',
    loadComponent: () => import('./pages-utils/notifications/notifications.component').then(m => m.NotificationsComponent),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, SubscriptionActiveGuard, internalOnlyGuard],
    title: 'Notifications',
    data: { pageHeader: { breadcrumb: 'Notifications' } }
  },
  {
    path: 'admin',
    loadChildren: () => import('./modules/admin/admin.routes').then(mod => mod.AdminRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard],
    data: { authorizations: [Role.SuperAdmin] }
  },
  {
    path: 'mokah-admin',
    loadChildren: () => import('./modules/mokah-admin/mokah-admin.routes').then(mod => mod.MokahAdminRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, waAdminGuard],
  },
  // Hub Développeur (MOKA-305) : localhost pour tous, dev et prod pour les Administrateurs Mokah+.
  {
    path: 'developpeur',
    loadChildren: () => import('./modules/ui/ui.routes').then(mod => mod.UiRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard, developerHubGuard],
    data: { pageHeader: { breadcrumb: 'Développeur', breadcrumbLink: false } }
  },
  // Anciennes adresses (/ui) et variante d'orthographe.
  { path: 'ui', redirectTo: 'developpeur', pathMatch: 'full' },
  { path: 'ui/:page', redirectTo: 'developpeur/:page' },
  { path: 'developper', redirectTo: 'developpeur', pathMatch: 'full' },
  { path: 'developper/:page', redirectTo: 'developpeur/:page' },
  {
    path: 'login',
    redirectTo: 'sign-in',
  },
  {
    // Pas de canMatch maintenanceGuard : les WA_ADMIN doivent pouvoir se connecter pendant la maintenance
    path: 'sign-in',
    component: SignInComponent,
    canActivate: [isNotConnectedGuard],
    title: 'Connexion',
  },
  {
    // Pas de canMatch maintenanceGuard : meme raison que sign-in
    path: 'sign-up',
    component: SignInComponent,
    canActivate: [isNotConnectedGuard],
    title: 'Inscription',
    data: { activeTab: 'signUp' },
  },
  {
    path: 'oauth/authorize',
    loadComponent: () => import('./pages-utils/auth/oauth-authorize/oauth-authorize.component').then(m => m.OauthAuthorizeComponent),
    canMatch: [maintenanceGuard],
    title: "Autoriser l'accès",
  },
  {
    // Pas de canMatch : callback auth Supabase (confirmation email, session PASSWORD_RECOVERY autorisée)
    path: 'auth/callback',
    component: AuthCallbackComponent,
    title: 'Activation du compte'
  },
  {
    path: 'subscriptions',
    loadChildren: () => import('./modules/subscriptions/subscriptions.routes').then(m => m.SubscriptionsRoutes),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard]
  },
  {
    // Pas de canMatch : PASSWORD_RECOVERY crée une session temporaire qui bloquerait
    path: 'reset-password',
    component: ResetPasswordComponent,
    title: 'Réinitialisation du mot de passe'
  },
  {
    // Pas de canMatch : idem reset-password
    path: 'reset-password/:params',
    component: ResetPasswordComponent,
    title: 'Réinitialisation du mot de passe'
  },
  // ── Documents légaux (MOKA-212) ───────────────────────────────────────────────
  // Anciennes URLs dédiées conservées comme filet de sécurité pour les liens déjà
  // diffusés (emails, moteurs de recherche) : pas de canMatch ici, la destination
  // /documents/:slug porte déjà maintenanceGuard.
  {
    path: 'privacy-policy',
    redirectTo: '/documents/privacy-policy',
    pathMatch: 'full'
  },
  {
    path: 'legal-notices',
    redirectTo: '/documents/legal-notices',
    pathMatch: 'full'
  },
  {
    path: 'terms',
    redirectTo: '/documents/terms',
    pathMatch: 'full'
  },
  {
    // Désinscription d'un type d'e-mail depuis le lien de pied d'e-mail (MOKA-255) : publique.
    path: 'email/unsubscribe',
    loadComponent: () => import('./pages-utils/email-unsubscribe/email-unsubscribe.component').then(m => m.EmailUnsubscribeComponent),
    canMatch: [maintenanceGuard]
  },
  {
    path: 'documents',
    loadComponent: () => import('./pages-utils/legal-documents-hub/legal-documents-hub.component').then(m => m.LegalDocumentsHubComponent),
    canMatch: [maintenanceGuard]
  },
  {
    path: 'documents/:slug',
    loadComponent: () => import('./pages-utils/legal-document-by-slug/legal-document-by-slug.component').then(m => m.LegalDocumentBySlugComponent),
    canMatch: [maintenanceGuard]
  },
  {
    // Module absent de l'abonnement (isModuleEnabledGuard, MOKA-402).
    path: 'module-inactif/:module',
    loadComponent: () => import('./pages-utils/module-inactive/module-inactive.component').then(m => m.ModuleInactiveComponent),
    canMatch: [maintenanceGuard],
    canActivate: [authGuard],
    title: 'Module non activé'
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./pages-utils/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
    canMatch: [maintenanceGuard]
  },
  {
    // Tablette d'affichage à l'entrée d'une salle (MOKA-243) : publique, sans
    // compte ni layout (cf. AppComponent.onBarePage), authentifiée par le jeton
    // d'écran remis à l'appairage. Pas de maintenanceGuard : un écran mural ne
    // doit pas rester bloqué sur /maintenance.
    path: 'salle/affichage',
    loadComponent: () => import('./modules/room-reservations/pages/room-display/room-display.component').then(m => m.RoomDisplayComponent),
    title: 'Écran de salle — Mokah+',
  },
  {
    path: 'room-reservations',
    loadChildren: () => import('./modules/room-reservations/room-reservations.routes').then(mod => mod.RoomReservationsRoutes),
    canMatch: [maintenanceGuard],
    // Décision 7 (docs/modules/ROOMS.md) : les Salles font partie du module Calendrier.
    canActivate: [authGuard, SubscriptionActiveGuard, isModuleEnabledGuard],
    data: { moduleKey: 'calendars_enable' },
  },
  ...PROJECT_MANAGEMENT_ROUTES,
  {
    path: '404',
    loadComponent: () => import('./pages-utils/not-found/not-found.component').then(m => m.NotFoundComponent),
    canMatch: [maintenanceGuard]
  },
  ...routesSidebar,
  {
    path: '**',
    redirectTo: '/404'
  }
]
